import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ActiveListingProvider } from './contexts/ActiveListingContext';
import { Toaster } from 'sonner';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { DashboardPage } from './pages/DashboardPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { AICaptionPage } from './pages/AICaptionPage';
import { AuthCallback } from './pages/AuthCallback';
import { AdminTemplatesPage } from './pages/AdminTemplatesPage';
import AdminUsersPage from './pages/AdminUsersPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { CreateCampaignPage } from './pages/CreateCampaignPage';
import { EmailListsPage } from './pages/EmailListsPage';
import { SocialAccountsPage } from './pages/SocialAccountsPage';
import { ProfilePage } from './pages/ProfilePage';
import { AccountSettingsPage } from './pages/AccountSettingsPage';
import { CanvasDesignerPage } from './pages/CanvasDesignerPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { PrintCenterPage } from './pages/PrintCenterPage';
import { MagicCreatePage } from './pages/MagicCreatePage';
import { FacebookCallback } from './pages/FacebookCallback';
import ReelEditorPage from './pages/ReelEditorPage';
import AdminReelTemplatesPage from './pages/AdminReelTemplatesPage';
import ScheduledPostsPage from './pages/ScheduledPostsPage';
import TeamManagementPage from './pages/TeamManagementPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import CarouselEditorPage from './pages/CarouselEditorPage';
import DemoPreviewPage from './pages/DemoPreviewPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DataDeletionPage from './pages/DataDeletionPage';
import AdminCarouselTemplatesPage from './pages/AdminCarouselTemplatesPage';
import MyReelsPage from './pages/MyReelsPage';
import MyCarouselsPage from './pages/MyCarouselsPage';
import DesignStudioPage from './pages/DesignStudioPage';
import ClipperSetupPage from './pages/ClipperSetupPage';
import SupportPage from './pages/SupportPage';
import AdminSupportPage from './pages/AdminSupportPage';
import { SupportChatWidget } from './components/SupportChatWidget';
import MobileCreatePage from './pages/MobileCreatePage';
import MobileMenuPage from './pages/MobileMenuPage';
import { PlanProvider } from './contexts/PlanContext';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // If user data passed from AuthCallback, skip auth check
  if (location.state?.user) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (user) {
    // Respect ?next= so links like "/login?next=/templates" land users on the intended page.
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    return <Navigate to={next || '/dashboard'} replace />;
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/demo" element={<DemoPreviewPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/ai-caption" element={<ProtectedRoute><AICaptionPage /></ProtectedRoute>} />
      <Route path="/admin/templates" element={<ProtectedRoute><AdminTemplatesPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/create-campaign/:templateId" element={<ProtectedRoute><CreateCampaignPage /></ProtectedRoute>} />
      <Route path="/email-lists" element={<ProtectedRoute><EmailListsPage /></ProtectedRoute>} />
      <Route path="/social-accounts" element={<ProtectedRoute><SocialAccountsPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/account-settings" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
      <Route path="/canvas-designer" element={<ProtectedRoute><CanvasDesignerPage /></ProtectedRoute>} />
      <Route path="/canvas-designer/:templateId" element={<ProtectedRoute><CanvasDesignerPage /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/subscription/cancel" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/print-center" element={<ProtectedRoute><PrintCenterPage /></ProtectedRoute>} />
      <Route path="/magic-create" element={<ProtectedRoute><MagicCreatePage /></ProtectedRoute>} />
      <Route path="/reel-editor" element={<ProtectedRoute><ReelEditorPage /></ProtectedRoute>} />
      <Route path="/admin/reel-templates" element={<ProtectedRoute><AdminReelTemplatesPage /></ProtectedRoute>} />
      <Route path="/scheduled-posts" element={<ProtectedRoute><ScheduledPostsPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamManagementPage /></ProtectedRoute>} />
      <Route path="/carousel-editor" element={<ProtectedRoute><CarouselEditorPage /></ProtectedRoute>} />
      <Route path="/admin/carousel-templates" element={<ProtectedRoute><AdminCarouselTemplatesPage /></ProtectedRoute>} />
      <Route path="/my-reels" element={<ProtectedRoute><MyReelsPage /></ProtectedRoute>} />
      <Route path="/my-carousels" element={<ProtectedRoute><MyCarouselsPage /></ProtectedRoute>} />
      <Route path="/design-studio" element={<ProtectedRoute><DesignStudioPage /></ProtectedRoute>} />
      <Route path="/clipper-setup" element={<ProtectedRoute><ClipperSetupPage /></ProtectedRoute>} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/admin/support" element={<ProtectedRoute><AdminSupportPage /></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><MobileCreatePage /></ProtectedRoute>} />
      <Route path="/menu" element={<ProtectedRoute><MobileMenuPage /></ProtectedRoute>} />
      <Route path="/callback" element={<OAuthCallbackPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/facebook/callback" element={<FacebookCallback />} />
      {/* Catch-all: unknown routes redirect to dashboard (logged in) or landing.
          ProtectedRoute will bounce unauthenticated users to /login. */}
      <Route path="*" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ActiveListingProvider>
        <BrowserRouter>
          <PlanProvider>
            <div className="App">
              <div className="noise-texture" />
              <Toaster position="top-right" />
              <AppRoutes />
              <SupportChatWidget />
            </div>
          </PlanProvider>
        </BrowserRouter>
      </ActiveListingProvider>
    </AuthProvider>
  );
}

export default App;